import React, { useState, useEffect } from 'react';
import { securePut, secureGet, securePost } from '../../config/api';
import { SaveCancel } from '../../components/PrimaryButton';
import { useHistory } from 'react-router-dom';
import { formatAmount } from "../../utils/format";
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Helmet } from "react-helmet-async";
import { useLocation } from 'react-router-dom';
import { PrimaryButton } from '../../components/PrimaryButton';
import Input from '../../components/Input';
import SnackbarNotification from '../../components/SnackbarNotification';
import Breadcrumbs from '../../components/Breadcrumbs';
import LoadingLogo from '../../components/LoadingLogo';
import GeneratePopup from "../../components/GeneratePopUp";
import { PAYMENT_ENUMS } from "../../utils/paymentModes";

import plusIcon from '../../icons/plus.svg';
import DeleteIcon from '../../icons/trashR.svg';
import plus from '../../icons/plusP.svg';
import minus from '../../icons/minusP.svg';
import plusY from '../../icons/plusY.svg';
import minusY from '../../icons/minusY.svg';
import noImage from '../../icons/noImageIcon.svg';
import EyeIcon from '../../icons/eyeB.svg';
import discountIcon from '../../icons/discount.svg';

const QuoteForm = ({
    title,
    baseFields = [],
    listFields = [],
    additionalFields = [],
    products = [],
    onSuccess,
}) => {
    const [formValues, setFormValues] = useState({});
    const [listEntities, setListEntities] = useState([]);
    const [currentEntity, setCurrentEntity] = useState({});
    const [totalHT, setTotalHT] = useState(0);
    const [totalTTC, setTotalTTC] = useState(0);
    const [errorMessage, setErrorMessage] = useState("");
    const history = useHistory();
    const { t, i18n } = useTranslation();
    const { id } = useParams();
    const location = useLocation();
    const [quote, setQuote] = useState(null);
    const [isPopupOpen, setPopupOpen] = useState(false);
    const [generatingBC, setGeneratingBC] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const showSnackbar = (message, severity = 'success', manual = false) => {
        setSnackbar({ open: true, message, severity, manual });
    };


    useEffect(() => {
        const checkDataReadiness = () => {
            const baseFieldsReady = baseFields.every(field =>
                !field.options || field.options.length > 0
            );

            if (id) {
                return quote && baseFieldsReady;
            }
            return baseFieldsReady;
        };

        if (checkDataReadiness()) {
            setLoading(false);
        } else {
            setLoading(true);
        }
    }, [quote, baseFields, id]);

    useEffect(() => {
        const abortController = new AbortController();
        const fetchQuote = async () => {
            if (!id) return;
            try {
                const response = await secureGet(`/quotes/${id}`, showSnackbar, { signal: abortController.signal });
                setQuote(response.data);
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error("Failed to fetch quote:", error);
                }
            }
        };

        fetchQuote();
        return () => {
            abortController.abort();
        };
    }, [id]);

    useEffect(() => {
        if (quote) {
            const initialValues = {
                clientId: quote.client?.id,
                repoId: quote.repo?.id || "",
                comment: quote.comment || "",
                paymentMode: quote.paymentMode || "",
                additionalFields: quote.fields || {},
            };
            setFormValues(initialValues);

            const productList = quote.details.map((detail) => ({
                product: detail.product,
                quantity: detail.quantity,
                discount: detail.salesDiscount || 0,
            }));
            setListEntities(productList);
        }
    }, [quote]);

    useEffect(() => {
        const calculatedTotalHT = listEntities.reduce(
            (sum, entity) =>
                sum +
                entity.quantity * (entity.product.priceHT * (1 - entity.discount / 100)),
            0
        );

        const calculatedTotalTTC = listEntities.reduce(
            (sum, entity) =>
                sum +
                entity.quantity * (entity.product.priceTTC * (1 - entity.discount / 100)),
            0
        );

        setTotalHT(calculatedTotalHT.toFixed(2));
        setTotalTTC(calculatedTotalTTC.toFixed(2));
    }, [listEntities]);

    const handleInputChange = (name, value) => {
        setFormValues((prev) => ({ ...prev, [name]: value }));
    };

    const removeListEntity = (index) => {
        setListEntities((prevEntities) =>
            prevEntities.filter((_, i) => i !== index)
        );
    };

    const handleQuantityChange = (index, newQuantity) => {
        setListEntities((prevEntities) => {
            const updatedEntities = [...prevEntities];
            updatedEntities[index].quantity = Math.max(0, newQuantity);
            return updatedEntities;
        });
    };

    const handleDiscountChange = (index, change) => {
        setListEntities((prevEntities) =>
            prevEntities.map((entity, i) => {
                if (i === index) {
                    return {
                        ...entity,
                        discount: Math.max(0,
                            parseFloat((entity.discount + change).toFixed(1))
                        )
                    };
                }
                return entity;
            })
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // In modify mode, listFields are not required if listEntities already exist
        const fieldsToValidate = listEntities.length > 0
            ? [...baseFields, ...additionalFields]
            : [...baseFields, ...listFields, ...additionalFields];

        const missing = fieldsToValidate.filter(f => f.required && !formValues[f.name]);
        if (missing.length > 0) {
            showSnackbar(`Veuillez remplir les champs obligatoires : ${missing.map(f => f.label).join(', ')}`, 'error');
            return;
        }

        const payload = {
            ...(quote?.id && { quoteId: quote.id }),
            clientId: formValues.clientId,
            comment: formValues.comment,
            repoId: formValues.repoId,
            details: listEntities.map((entity) => ({
                productId: entity.product?.id,
                quantity: entity.quantity,
                salesDiscount: entity.discount,
            })),
        };

        setSaving(true);
        try {
            await securePut("/quotes/add", payload, showSnackbar);
            onSuccess();
            history.push({
                pathname: "/quotes",
                state: {
                    filters: location.state?.filters || {},
                    page: location.state?.page || 0,
                    size: location.state?.size || 30,
                },
            });
            setFormValues({});
            setListEntities([]);
        } catch (error) {
            console.error("Failed to submit quote:", error);
        } finally {
            setSaving(false);
        }
    };

    const handleGenerateBC = () => {
        setPopupOpen(true);
    };

    const handleGenerateConfirm = async () => {
        const enumPayment = formValues.paymentMode;

        if (!enumPayment || !PAYMENT_ENUMS.includes(enumPayment)) {
            showSnackbar(t("errors.invalid_payment_mode") || "Invalid payment mode", "error");
            return;
        }

        const payload = {
            ...(quote?.id && { quoteId: quote.id }),
            repoId: formValues.repoId,
            paymentMode: enumPayment,
            payed: true,
            status: null,
            fields: formValues.additionalFields || {},
        };

        setGeneratingBC(true);
        try {
            await securePost("/quotes/toCart", payload, showSnackbar);
            showSnackbar(t("confirmation.quoteBCGenerated"), "success");
            setPopupOpen(false);
            history.push(`/quotes/details/${quote.id}`);
        } catch (error) {
            console.error("Failed to submit quote:", error);
        } finally {
            setGeneratingBC(false);
        }
    };

    const handleCancelGenerate = () => {
        setPopupOpen(false);
    };

    const addListEntity = () => {
        const { product, quantity } = currentEntity;
        if (!product || !quantity || quantity <= 0) {
            showSnackbar(t('placeholder.empty_fields'), "error");
            return;
        }

        const selectedProduct = products.find((p) => p.value === product);
        const newQuantity = parseInt(quantity, 10);

        setListEntities((prev) => {
            // Check if product already exists in the list
            const existingIndex = prev.findIndex(
                (entity) => entity.product?.id === selectedProduct.fullEntity.id
            );

            if (existingIndex !== -1) {
                // Update existing product quantity
                const updatedEntities = [...prev];
                updatedEntities[existingIndex] = {
                    ...updatedEntities[existingIndex],
                    quantity: updatedEntities[existingIndex].quantity + newQuantity,
                };
                return updatedEntities;
            } else {
                // Add new product
                const newEntity = {
                    product: selectedProduct.fullEntity,
                    quantity: newQuantity,
                    discount: 0,
                };
                return [...prev, newEntity];
            }
        });
        setCurrentEntity({});
    };

    const handleCancel = () => {
        if (location.pathname.includes("details")) {
            history.push(`/quotes/details/${quote.id}`);
        } else {
            history.push({
                pathname: "/quotes",
                state: {
                    filters: location.state?.filters || {},
                    page: location.state?.page || 0,
                    size: location.state?.size || 30,
                },
            });
        }
    };

    const handleImageError = (e) => {
        e.target.src = noImage;
        e.target.style.width = "25px";
        e.target.style.height = "25px";
        e.target.style.objectFit = "contain";
    };

    const filteredBaseFields = quote
        ? baseFields
        : baseFields.filter(field => field.name !== 'paymentMode');

    return (
        <div className="   ">
            <Helmet>
                <title>GrayFox - {t("titles.quotes")}</title>
            </Helmet>
            <Breadcrumbs />

            <div className="flex justify-between items-center mb-4">
                <h2 className="text-[32px] text-[#38485C] font-semibold">{title}</h2>
                {
                    quote && (quote.cart === null) && (
                        <PrimaryButton
                            label={t(`primaryButtons.generateBC`)}
                            icon={EyeIcon}
                            onClick={handleGenerateBC}
                        />
                    )}
            </div>
            {loading ? (
                <div className="flex justify-center items-center h-[300px]">
                    <LoadingLogo />
                </div>
            ) : (
                <form className="space-y-6">
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {filteredBaseFields.map((field, index) => (
                                <div key={index}>
                                    <Input
                                        type={field.type || "text"}
                                        label={field.label}
                                        name={field.name}
                                        value={formValues[field.name] || ""}
                                        options={field.options || []}
                                        required={field.required}
                                        onChange={(value) => handleInputChange(field.name, value)}
                                    />
                                </div>
                            ))}
                        </div>
                        {errorMessage && (
                            <p className="mt-2 text-sm text-red-500 font-semibold">{errorMessage}</p>
                        )}
                    </div>

                    {additionalFields.length > 0 && (
                        <div className="space-y-[10px]">
                            <h3 className="text-[16px] font-semibold text-[#ACAFC6]">{t(`fields_types.additional`)}</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                {additionalFields.map((field, index) => (
                                    <Input
                                        key={index}
                                        label={field.label}
                                        name={field.name}
                                        type={field.type || "text"}
                                        options={field.options || []}
                                        required={field.required}
                                        value={formValues[field.name] || ""}
                                        onChange={(value) => handleInputChange(field.name, value)}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {listFields.length > 0 && (
                        <div className="space-y-[10px]">
                            <h3 className="text-[16px] font-semibold text-[#ACAFC6]">{t(`fields_types.products`)}</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                {listFields.map((field, index) => (
                                    <Input
                                        key={index}
                                        type={field.type || 'text'}
                                        label={field.label}
                                        name={field.name}
                                        value={currentEntity[field.name] || ''}
                                        required={field.required}
                                        onChange={(value) => setCurrentEntity((prev) => ({ ...prev, [field.name]: value }))}
                                        options={field.name === 'product'
                                            ? products.map((product) => ({
                                                value: product.value,
                                                name: product.name,
                                                reference: product.reference,
                                                fullEntity: product.fullEntity,
                                            }))
                                            : field.options || []}
                                        disabled={!formValues.clientId}
                                    />
                                ))}
                                <button
                                    type="button"
                                    onClick={addListEntity}
                                    className="bg-[#043F88] text-white w-[48px] h-[48px] rounded-md hover:bg-[#3F3456] flex items-center justify-center"
                                >
                                    <img src={plusIcon} alt="Add" className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* List Entity List */}
                    {listEntities.length > 0 && (
                        <div className="space-y-[8px] mt-6">
                            {listEntities.map((entity, index) => {
                                const discountMultiplier = 1 - (entity.discount || 0) / 100;
                                const discountedPriceHT =
                                    entity.discount > 0
                                        ? (entity.product.priceHT * discountMultiplier).toFixed(2)
                                        : entity.product.priceHT?.toFixed(2);
                                const discountedPriceTTC =
                                    entity.discount > 0
                                        ? (entity.product.priceTTC * discountMultiplier).toFixed(2)
                                        : entity.product.priceTTC?.toFixed(2);

                                return (
                                    <div
                                        key={index}
                                        className="grid grid-cols-[30%_12%_12%_13%_13%_10%_10%] items-center py-2 px-4 rounded-lg bg-white shadow-[0px_6px_26px_rgba(98,107,125,0.07)] gap-0"
                                    >
                                        {/* Product Info */}
                                        <div className="flex items-center gap-2">
                                            <div className="w-[76px] h-[76px] flex items-center justify-center  shadow-[0px_6px_26px_rgba(98,107,125,0.07)] rounded-[12px] overflow-hidden">
                                                {entity.product?.image ? (
                                                    <img
                                                        src={entity.product.image}
                                                        alt={entity.product.name}
                                                        className="w-full h-full object-contain"
                                                        onError={handleImageError}
                                                    />
                                                ) : (
                                                    <div className="w-full h-full bg-white"></div>
                                                )}
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <p className="text-[12px] font-bold text-[#ACAFC6]">{entity.product?.reference}</p>
                                                    {entity.product?.discount > 0 && (
                                                        <p className="inline-flex items-center gap-0.5 text-[9px] bg-[#FFD700] rounded-[12px] px-1 py-0.5 font-bold text-[#043F88]">
                                                            <img src={discountIcon} alt="discount" className="w-2 h-2" />
                                                            <span>{entity.product?.discount}%</span>
                                                        </p>
                                                    )}
                                                </div>
                                                <p className="text-secondary font-semibold text-[16px]">
                                                    {entity.product?.name || "Produit"}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Prix HT */}
                                        <div className="text-center">
                                            <p className="text-[#707070] font-semibold text-[12px]">{t(`rows.priceuHT`).toUpperCase()}</p>
                                            <p className="text-[#043F88] font-bold text-lg">
                                                {formatAmount(discountedPriceHT)}
                                            </p>
                                            {entity.discount > 0 && (
                                                <p className="text-[#ACAFC6] text-sm line-through">
                                                    {formatAmount(entity.product?.priceHT)}
                                                </p>
                                            )}
                                        </div>

                                        {/* Prix TTC */}
                                        <div className="text-center">
                                            <p className="text-[#707070] font-semibold text-[12px]">{t(`rows.priceuTTC`).toUpperCase()}</p>
                                            <p className="text-[#043F88] font-bold text-lg">
                                                {formatAmount(discountedPriceTTC)}
                                            </p>
                                            {entity.discount > 0 && (
                                                <p className="text-[#ACAFC6] text-sm line-through">
                                                    {formatAmount(entity.product?.priceTTC)}
                                                </p>
                                            )}
                                        </div>

                                        {/* Remise */}
                                        <div className="flex flex-col items-center">
                                            <p className="text-[#707070] font-semibold text-[12px] mb-1">{t(`rows.remise%`)}</p>
                                            <div className="flex items-stretch rounded-[12px] border border-[#fea26f] bg-[#fffaf8] divide-x divide-[#fea26f]">
                                                <button
                                                    type="button"
                                                    onClick={() => handleDiscountChange(index, -0.1)}
                                                    className="w-8 h-8 flex items-center justify-center text-orange-500 font-bold"
                                                >
                                                    <img src={minusY} alt="Minus" className="w-4 h-4" />
                                                </button>
                                                <span className="w-10 h-8 flex items-center justify-center text-[#38485C] font-semibold text-[14px]">
                                                    {entity.discount?.toFixed(1) || "0.0"}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => handleDiscountChange(index, 0.1)}
                                                    className="w-8 h-8 flex items-center justify-center text-orange-500 font-bold"
                                                >
                                                    <img src={plusY} alt="Plus" className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Quantité */}
                                        <div className="flex flex-col items-center">
                                            <p className="text-[#707070] font-semibold text-[12px] mb-1">{t(`rows.quantity`).toUpperCase()}</p>
                                            <div className="flex items-stretch rounded-[12px] border border-secondary bg-secondary/20 divide-x divide-secondary">
                                                <button
                                                    type="button"
                                                    onClick={() => handleQuantityChange(index, Math.max(entity.quantity - 1, 1))}
                                                    className="w-8 h-8 flex items-center justify-center"
                                                >
                                                    <img src={minus} alt="Minus" className="w-4 h-4" />
                                                </button>
                                                <span className="w-10 h-8 flex items-center justify-center font-semibold text-[#38485C] text-[14px]">
                                                    {entity.quantity}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => handleQuantityChange(index, entity.quantity + 1)}
                                                    className="w-8 h-8 flex items-center justify-center"
                                                >
                                                    <img src={plus} alt="Plus" className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Prix Total */}
                                        <div className="text-center">
                                            <p className="text-[#707070] font-semibold text-[12px]">{t(`rows.totalPrice`)}</p>
                                            <p className="text-[#043F88] font-bold text-lg">
                                                {formatAmount(entity.quantity * discountedPriceTTC)}
                                            </p>
                                            {entity.discount > 0 && (
                                                <p className="text-[#ACAFC6] text-sm line-through">
                                                    {formatAmount(entity.quantity * entity.product?.priceTTC)}
                                                </p>
                                            )}
                                        </div>

                                        {/* Delete Icon */}
                                        <div className="flex justify-center">
                                            <button
                                                type="button"
                                                onClick={() => removeListEntity(index)}
                                                className="w-8 h-8 flex items-center justify-center bg-[#F9FAFC] rounded-[12px] border border-[#EF466F4D]"
                                            >
                                                <img src={DeleteIcon} alt="Delete" className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Totals and Footer */}
                    <div className="flex justify-between items-center mt-6">
                        <div className="flex text-left">
                            <p className="text-[#231E29] text-[16px] font-medium">
                                TOTAL HT:
                            </p>
                            <p className="text-[#043F88] text-[16px] font-bold pl-2">
                                {formatAmount(totalHT)}
                            </p>
                            <p className="text-[#231E29] text-[16px] font-medium pl-[50px]">
                                TOTAL TTC:
                            </p>
                            <p className="text-[#043F88] text-[16px] font-bold pl-2">
                                {formatAmount(totalTTC)}
                            </p>
                        </div>
                        <div className="space-x-4">
                            <SaveCancel
                                onSaveClick={handleSubmit}
                                onCancelClick={handleCancel}
                                saving={saving}
                            />
                        </div>
                    </div>
                </form>
            )}

            <GeneratePopup
                open={isPopupOpen}
                entity={t(`entity.bc`)}
                onConfirm={handleGenerateConfirm}
                onCancel={handleCancelGenerate}
                loading={generatingBC}
            />
            <SnackbarNotification
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                manual={snackbar.manual}
            />
        </div>
    );
};

export default QuoteForm;